import type { ReactNode } from 'react';
import React from 'react';
import styled, { css } from 'styled-components';
import { GetColor } from '../../../style/color';
import { CheckboxWrapper } from '../../checkbox-wrapper';
import type { DropMenuCheckboxItem } from '../types';
import { toClassName } from 'venn-utils';
import EllipsisTooltipSpan from '../../ellipsis-tooltip/EllipsisTooltipSpan';
import { isNil } from 'lodash';
import { TooltipPosition } from '../../enums';
import classNames from 'classnames';

export interface CheckboxMenuItemProps<T = string> {
  item: DropMenuCheckboxItem<T>;
  onCheck: () => void;
  onOnly?: () => void;
  showLines?: boolean;
  maxWidth?: number;
  hideCheckbox?: boolean;
  isFirst?: boolean;
  isReadOnly?: boolean;
  children?: ReactNode;
  tooltipPortal?: boolean;
  refreshedStyling?: boolean;
}

const ONLY_BUTTON_WIDTH = 53;
const LEVEL_INDENT = 20;
const CONTEXT_CHILD_FACTOR_PADDING = 10;
const TOOLTIP_MAX_WIDTH = 320;

class CheckboxMenuItem<T> extends React.PureComponent<CheckboxMenuItemProps<T>, { hovered: boolean }> {
  state = { hovered: false };

  onMouseEnter = () => this.setState({ hovered: true });

  onMouseLeave = () => this.setState({ hovered: false });

  render() {
    const {
      item,
      onCheck,
      onOnly,
      showLines,
      maxWidth,
      hideCheckbox,
      isFirst,
      isReadOnly,
      tooltipPortal = false,
      refreshedStyling,
    } = this.props;
    const {
      label,
      level,
      checked,
      description,
      disabled,
      className,
      icon,
      badge,
      tooltipContent,
      alwaysShowTooltip,
      contextualChildItems,
    } = item;
    const qaClass = toClassName(label);
    const showOnly = !isNil(onOnly) && !hideCheckbox;
    const isRowActive = hideCheckbox && checked;
    const { hovered } = this.state;
    return (
      <>
        <FilterCheckboxRow
          level={level}
          className={classNames(
            'qa-dropmenu-checkbox-item',
            qaClass,
            className,
            isRowActive && 'qa-dropmenu-checkbox-item-active',
          )}
          disabled={disabled}
          active={isRowActive}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          {showLines && level !== undefined && (
            <Lines level={level}>
              {Array(level)
                .fill(0)
                .map((_, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Line key={idx} level={idx} />
                ))}
            </Lines>
          )}
          <CheckboxWrapper
            checked={checked}
            onChange={onCheck}
            onClick={hideCheckbox && !isNil(onOnly) ? onOnly : undefined}
            indeterminate={item.indeterminate}
            disabled={disabled || isReadOnly}
            hideCheckbox={hideCheckbox}
            justify={maxWidth ? 'left' : 'center'}
            refreshedStyling={refreshedStyling}
            inputColor={refreshedStyling ? GetColor.DarkBlue : undefined}
          >
            {maxWidth ? (
              <EllipsisTooltipSpan
                maxWidth={maxWidth - (level ?? 0) * LEVEL_INDENT - (showOnly && hovered ? ONLY_BUTTON_WIDTH : 0) - 43}
                tooltipMaxWidth={TOOLTIP_MAX_WIDTH}
                flex
                alwaysShowTooltip={alwaysShowTooltip}
                position={isFirst ? TooltipPosition.Bottom : TooltipPosition.Top}
                content={tooltipContent}
                usePortal={tooltipPortal}
              >
                {icon}
                {label}
                {badge}
              </EllipsisTooltipSpan>
            ) : (
              <>
                {icon}
                {label}
                {badge}
              </>
            )}
          </CheckboxWrapper>
          {showOnly && !disabled && !isReadOnly ? (
            <Only
              className="qa-checkbox-item-only"
              onClick={(e) => {
                e.stopPropagation();
                onOnly?.();
              }}
            >
              only
            </Only>
          ) : null}
        </FilterCheckboxRow>
        {description ? (
          <FilterCheckboxRow>
            <Description>{description}</Description>
          </FilterCheckboxRow>
        ) : null}
        {(contextualChildItems || []).map((item) => (
          <ContextChildItem key={item} level={level}>
            {maxWidth !== undefined ? (
              <EllipsisTooltipSpan
                flex
                maxWidth={maxWidth - ((level ?? 0) + 1) * LEVEL_INDENT - CONTEXT_CHILD_FACTOR_PADDING - 40}
                position={isFirst ? TooltipPosition.Bottom : TooltipPosition.Top}
              >
                {item}
              </EllipsisTooltipSpan>
            ) : (
              item
            )}
          </ContextChildItem>
        ))}
      </>
    );
  }
}

interface ItemProps {
  level?: number;
  disabled?: boolean;
  active?: boolean;
}

const Description = styled.div`
  font-size: 10px;
  color: ${GetColor.GreyScale.Grey80};
  line-height: 1.4;
  padding: 0 24px;
`;

export const FilterCheckboxRow = styled.div<ItemProps>`
  position: relative;
  display: flex;
  justify-content: space-between;
  line-height: 24px;
  padding-left: 20px;
  label > span {
    flex: 1;
    font-size: 14px;
  }
  ${(props) => !isNil(props.level) && `padding-left: ${props.level * LEVEL_INDENT + 20}px;`}
  color: ${({ disabled }) => (disabled ? GetColor.GreyScale.Grey40 : GetColor.Black)};
  :hover {
    background-color: ${GetColor.GreyScale.Grey10};
  }
  ${({ active }) =>
    active &&
    css`
      background-color: ${GetColor.GreyScale.Grey10};
    `}
`;

const ContextChildItem = styled.div<ItemProps>`
  display: flex;
  justify-content: space-between;
  line-height: 24px;
  padding-left: 20px;
  label > span {
    flex: 1;
    font-size: 14px;
  }
  ${(props) =>
    !isNil(props.level) && `padding-left: ${(props.level + 1) * LEVEL_INDENT + CONTEXT_CHILD_FACTOR_PADDING + 20}px;`}
  color: ${({ disabled }) => (disabled ? GetColor.GreyScale.Grey40 : GetColor.Black)};
  :hover {
    background-color: ${GetColor.GreyScale.Grey10};
  }
  ${({ active }) =>
    active &&
    css`
      background-color: ${GetColor.GreyScale.Grey10};
    `}
`;

const Lines = styled.div<{ level: number }>`
  position: relative;
  ${({ level }) => css`
    margin-left: -${level * LEVEL_INDENT}px;
    width: ${level * LEVEL_INDENT}px;
  `}
`;

const Line = styled.div<{ level: number }>`
  border-left: 1px dashed ${GetColor.GreyScale.Grey40};
  position: absolute;
  height: 110%;
  ${({ level }) => css`
    left: ${level * LEVEL_INDENT + 7}px;
  `}
`;

const Only = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  opacity: 0;
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${GetColor.Primary.Dark};
  font-size: 11px;
  cursor: pointer;
  width: ${ONLY_BUTTON_WIDTH}px;
  padding-left: 10px;
  padding-right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  background-color: ${GetColor.GreyScale.Grey10};
  /* stylelint-disable-next-line */
  ${FilterCheckboxRow}:hover & {
    opacity: 1;
  }
`;

export default CheckboxMenuItem;
